// ===================== First-run tutorial (contextual hints) =====================
// Non-blocking sequenced hints shown during the player's first-ever run.
// Each step waits for the matching action (or a timeout) before advancing,
// then never appears again (persisted via Save). Skippable at any time.
import { Save } from '../engine/save.js';

export class Tutorial {
  constructor() {
    this.active = false;
    this.game = null;
    this.idx = -1;
    this.el = null;
    this.timer = 0;
    this.movedFor = 0;
    this._leveledOnce = false;
  }

  // Steps: text + a `done(game, tut, dt)` predicate that advances the sequence.
  // Every predicate has a timeout fallback — the MOVE hint once persisted
  // through boss fights because it only dismissed on movement.
  _steps() {
    // Same touch check the input engine uses: touch devices lead with the
    // touch instruction, not the keyboard.
    const touch = ('ontouchstart' in window) || navigator.maxTouchPoints > 0;
    return [
      {
        text: touch
          ? 'MOVE — touch &amp; drag anywhere (or gamepad stick)'
          : 'MOVE — <kbd>WASD</kbd> / arrows, or gamepad stick',
        done: (g, t, dt) => {
          const [mx, my] = g.input.moveVector();
          if (mx || my) t.movedFor += dt;
          return t.movedFor > 1.2 || t.timer > 12;
        },
      },
      {
        text: 'Weapons fire on their own — you dodge, position, and survive',
        done: (g, t) => t.timer > 4.5,
      },
      {
        text: 'Grab the ◆ gems enemies drop to gain XP',
        done: (g, t) => g.player.xp > 0 || g.player.level > 1 || t.timer > 10,
      },
      {
        text: 'Level up to choose new weapons, passives — and an element',
        done: (g, t) => t._leveledOnce || t.timer > 14,
      },
      {
        text: "Outlast the clock and the region's finale comes. Survive it and the hex is yours",
        done: (g, t) => t.timer > 5,
      },
    ];
  }

  start(game) {
    if (Save.data.tutorialDone) return;
    this.active = true;
    this.game = game;
    this.steps = this._steps();
    this.idx = 0;
    this.timer = 0;
    this.movedFor = 0;
    this._leveledOnce = false;
    this._show(this.steps[0].text);
  }

  // Called by main when the first level-up card has been picked.
  notifyLevelUp() { this._leveledOnce = true; }

  update(dt) {
    if (!this.active || !this.game) return;
    // A boss announce outranks tutorial chatter — the hint used to sit right
    // on top of the mobile boss HP bar. By the time a boss is up, the basics
    // have had their moment; end the tutorial rather than talk over it.
    if (this.game.boss && !this.game.boss.dead) { this.finish(); return; }
    this.timer += dt;
    const step = this.steps[this.idx];
    if (step && step.done(this.game, this, dt)) {
      this.idx++;
      this.timer = 0;
      if (this.idx >= this.steps.length) this.finish();
      else this._show(this.steps[this.idx].text);
    }
  }

  finish() {
    this.active = false;
    Save.data.tutorialDone = true;
    Save.flush();
    this._hide();
  }

  setVisible(v) { if (this.el) this.el.style.opacity = v ? '' : '0'; }

  _show(html) {
    if (!this.el) {
      this.el = document.createElement('div');
      this.el.id = 'tut-hint';
      this.el.addEventListener('click', (e) => {
        if (e.target.id === 'tut-skip') this.finish();
      });
      document.getElementById('game-root').appendChild(this.el);
    }
    const dots = this.steps.map((_, i) =>
      `<span class="tut-dot ${i <= this.idx ? 'on' : ''}"></span>`).join('');
    this.el.innerHTML =
      `<button id="tut-skip" aria-label="Skip tutorial">✕</button>` +
      `<div class="tut-text">${html}</div><div class="tut-dots">${dots}</div>`;
    this.el.classList.remove('pop');
    void this.el.offsetWidth;      // restart the pop animation
    this.el.classList.add('pop');
  }

  _hide() {
    if (this.el) { this.el.remove(); this.el = null; }
  }
}
